/**
 * AlertResultDialog — success/error result popup replacing easy-to-miss inline toasts.
 * One OK button; overlay click also closes. Parent conditionally renders it. The focus is on OK while it is up (what had it - a button the
 * keyboard would otherwise press from behind the overlay - does not keep it) and goes back to what had it when it closes (giveFocusBack).
 * OK is all there is to reach, so Tab stays on it, and Escape closes the alert and not the popup that is under it.
 */
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react'
import { useT } from '../../i18n/language-provider'
import { giveFocusBack } from './focus-return'
import { Icon } from './icon'

export interface AlertResult {
  variant: 'success' | 'error'
  message: ReactNode
}

interface Props extends AlertResult {
  onClose: () => void
}

export function AlertResultDialog({ variant, message, onClose }: Props) {
  const t = useT()
  const success = variant === 'success'
  const ok = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null
    ok.current?.focus()
    return () => giveFocusBack(previous)
  }, [])
  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'Tab') e.preventDefault()
    else if (e.key === 'Escape') { e.stopPropagation(); onClose() }
  }
  return (
    <div
      id="alert-result-dialog"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="alert-result-dialog-title"
      aria-describedby="alert-result-dialog-message"
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div className="w-full max-w-sm rounded-lg border border-border bg-surface p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-2 flex items-center gap-2">
          <span className={`flex h-7 w-7 items-center justify-center rounded-full text-sm font-bold text-white ${success ? 'bg-green-500' : 'bg-destructive'}`}>
            {success ? <Icon name="check" className="h-4 w-4" /> : <Icon name="alert" className="h-4 w-4" />}
          </span>
          <h2 id="alert-result-dialog-title" className="text-base font-semibold text-fg">{success ? t('common.success') : t('common.error')}</h2>
        </div>
        <div id="alert-result-dialog-message" className="mb-5 text-sm text-fg-soft">{message}</div>
        <div className="flex justify-end">
          <button
            id="alert-result-dialog-ok-btn"
            ref={ok}
            type="button"
            onClick={onClose}
            className="rounded bg-primary px-5 py-1.5 text-sm font-medium text-white hover:opacity-90"
          >
            {t('common.ok')}
          </button>
        </div>
      </div>
    </div>
  )
}
